import React from 'react'
import {
  AlignItems,
  Body2,
  Box,
  Col,
  Color,
  Display,
  Grid,
  Heading2,
  Heading3,
  Icon,
  IconVariant,
  JustifyContent,
  Link,
  Space,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { ContentstackImage } from '../ContentstackImage'
import { RichText } from '../../components/RichText'
import { CtaLink, mapCmsCta } from '../../cta'
import type { OnTriggerPromoCode } from '../../cta'

import type { ImageTextColumnsFields } from './types'

export type ImageTextColumnsProps = ImageTextColumnsFields & {
  onTriggerPromocode?: OnTriggerPromoCode
}

type OrderedColProps = React.ComponentProps<typeof Col> & {
  mobileOrder: number
}

function BaseOrderedCol({ mobileOrder: _mobileOrder, ...props }: OrderedColProps) {
  return <Col {...props} />
}

const OrderedCol = withCitrusTheme(BaseOrderedCol, (theme, props: OrderedColProps) => {
  const breakpointToLarge = `@media (max-width: ${theme.tokens.breakpoints[2]})`
  return {
    [breakpointToLarge]: { order: props.mobileOrder },
  }
})

const CitrusRichTextLink = withCitrusTheme(Link, (theme) => ({
  color: theme.color(Color.Secondary_400),
  textDecorationLine: 'underline',
}))

function SectionHeading({ level, children }: { level: 'h2' | 'h3'; children: React.ReactNode }) {
  if (level === 'h2') {
    return <Heading2>{children}</Heading2>
  }
  return <Heading3>{children}</Heading3>
}

export function ImageTextColumns({
  heading,
  heading_level,
  image_position,
  image,
  image_mobile,
  image_alt,
  body,
  sub_sections,
  closing_body,
  cta,
  onTriggerPromocode,
}: ImageTextColumnsProps) {
  const ctaLink = mapCmsCta(cta, onTriggerPromocode)
  const imageOnLeft = image_position === 'left'
  const hasSubSections = sub_sections && sub_sections.length > 0
  const shouldAddSubSectionCtaSpace = ctaLink && hasSubSections && !closing_body
  const textMobileOrder = 1
  const imageMobileOrder = 2

  const TextColumn = (
    <OrderedCol size={[12, null, null, 6]} mobileOrder={textMobileOrder}>
      <Box paddingRight={imageOnLeft ? 0 : [0, null, null, 10]}>
        <SectionHeading level={heading_level}>{heading}</SectionHeading>
        <Space size={2} />
        {body && (
          <Box paddingBottom={6}>
            <RichText
              content={body}
              paragraphGapSize={6}
              renderParagraph={({ children, key }) => (
                <Body2 key={key} size={4} style={{ whiteSpace: 'pre-wrap' }}>
                  {children}
                </Body2>
              )}
              renderLink={({ children, href, target, key }) => (
                <CitrusRichTextLink key={key} href={href} target={target}>
                  {children}
                </CitrusRichTextLink>
              )}
            />
          </Box>
        )}
        {hasSubSections && (
          <Box>
            {sub_sections?.map((item) => {
              const metadataUid = (item as { _metadata?: { uid: string } })._metadata?.uid

              return (
                <Box
                  key={metadataUid ?? item.body?.uid}
                  display={item.show_tick_icon ? Display.Flex : Display.Block}
                  paddingBottom={item.show_tick_icon ? 2 : 4}
                >
                  {item.show_tick_icon && (
                    <Box paddingRight={1}>
                      <Icon name="tick" variant={IconVariant.Confirmation} data-testid="tick" />
                    </Box>
                  )}
                  <Box>
                    <RichText
                      content={item.body}
                      paragraphGapSize={2}
                      renderParagraph={({ children, key }) => (
                        <Body2 key={key} size={4} style={{ whiteSpace: 'pre-wrap' }}>
                          {children}
                        </Body2>
                      )}
                      renderLink={({ children, href, target, key }) => (
                        <CitrusRichTextLink key={key} href={href} target={target}>
                          {children}
                        </CitrusRichTextLink>
                      )}
                    />
                  </Box>
                </Box>
              )
            })}
          </Box>
        )}
        {shouldAddSubSectionCtaSpace && <Space size={6} />}
        {closing_body && (
          <>
            <Space size={2} />
            <Box paddingBottom={6}>
              <Body2 size={4}>{closing_body}</Body2>
            </Box>
          </>
        )}
      </Box>
      {ctaLink && (
        <Box
          display={Display.Flex}
          justifyContent={[JustifyContent.Center, JustifyContent.FlexStart]}
          paddingBottom={[6, null, null, 0]}
        >
          <CtaLink cta={ctaLink} />
        </Box>
      )}
      <Space size={[6, null, null, 0]} />
    </OrderedCol>
  )

  const ImageColumn = (
    <OrderedCol
      size={[12, null, null, 6]}
      mobileOrder={imageMobileOrder}
      display={Display.Flex}
      alignItems={AlignItems.FlexStart}
      justifyContent={JustifyContent.Center}
    >
      <Box
        paddingRight={imageOnLeft ? [0, null, null, 10] : 0}
        maxWidth={[null, null, null, '600px']}
        width="100%"
      >
        <Box style={{ borderRadius: '8px', overflow: 'hidden' }}>
          <ContentstackImage
            alt={image_alt}
            fit="cover"
            sizes="(max-width: 1023px) 100vw, 600px"
            src={image.url}
            mobileSrc={image_mobile?.url}
            mobileMedia="(max-width: 1023px)"
            style={{ maxHeight: '457px' }}
            width="100%"
          />
        </Box>
      </Box>
    </OrderedCol>
  )

  return (
    <Box paddingV={[8, 12, 12, 16]} paddingH={[4, 6, 14]}>
      <Grid>
        {imageOnLeft ? ImageColumn : TextColumn}
        {imageOnLeft ? TextColumn : ImageColumn}
      </Grid>
    </Box>
  )
}
